import React, { useState } from "react";
import RoomAPI from "../../../RoomAPI";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import MenuFormPage from "../menu-page/menu-form-page";
import { updateError, updateLoading } from "../../../redux/slices/meta";
import { useToken } from "../../../authToken";
import { useUser } from "../auth/is_loggedin";
import { updateHiddenCode, updatePlayerId } from "../../../redux/slices/menu";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useSmartNavigate } from "../../../hooks/useSmartNavigate";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const EnterGameCode = () => {
  const lastRoomCode = localStorage.getItem("roomCode");
  const navigate = useSmartNavigate();
  const [searchParams] = useSearchParams();
  const isAudience = searchParams.get("audience") === "true";

  const { t } = useTranslation(["main", "common"]);

  const user = useUser();

  const token = useToken();
  const dispatch = useAppDispatch();
  const playerDetails = useAppSelector((state) => state.menu.playerDetails);

  const [roomCode, setRoomCode] = useState<string>(
    lastRoomCode ? lastRoomCode : ""
  );
  const [codeHidden, setCodeHidden] = useState(false);

  async function handleJoin() {
    if (!playerDetails) {
      alert("PLAYER NOT SET UP PROPERLY");
      return;
    }
    if (!roomCode) {
      alert("WHERE IS YOUR ROOMID");
      return;
    }
    try {
      dispatch(updateLoading(true));
      let roomPlayer;
      if (isAudience) {
        roomPlayer = await RoomAPI.JoinRoomAudience(
          roomCode,
          {
            meta: playerDetails.meta,
            user_id: user?.auth0Id,
            name: playerDetails.name,
            secret: playerDetails.secret,
          },
          token
        ).catch((reason) => {
          // COMPAT: handle both plain text (Go) and JSON (worker) error responses
          const data = reason.response?.data;
          const msg = typeof data === "string" ? data.trim() : data?.error ?? data;
          dispatch(updateError(msg != null ? JSON.stringify(msg) : reason.message || "Failed to join room."));
        });
      } else {
        roomPlayer = await RoomAPI.JoinRoom(
          roomCode,
          {
            meta: playerDetails.meta,
            user_id: user?.auth0Id,
            name: playerDetails.name,
            secret: playerDetails.secret,
          },
          token
        ).catch((reason) => {
          // COMPAT: handle both plain text (Go) and JSON (worker) error responses
          const data = reason.response?.data;
          const msg = typeof data === "string" ? data.trim() : data?.error ?? data;
          dispatch(updateError(msg != null ? JSON.stringify(msg) : reason.message || "Failed to join room."));
        });
      }

      if (roomPlayer) {
        let room = await RoomAPI.GetRoom(roomCode);
        dispatch(
          updatePlayerId({
            ...roomPlayer,
            room_id: room.id,
            room_code: room.code || roomCode,
          })
        );
        if (roomPlayer.secret) {
          localStorage.setItem("playerSecret", roomPlayer.secret);
        }
        const finalRoomCode = room.code || roomCode;
        localStorage.setItem("roomCode", finalRoomCode);
        navigate(`/game/${finalRoomCode}`);
      }
    } catch (err) {
      alert("FAILED TO JOIN LOBBY: " + err);
    } finally {
      dispatch(updateLoading(false));
    }
  }

  function handleCheck(checked: boolean) {
    setCodeHidden(checked);
    dispatch(updateHiddenCode(checked));
  }

  return (
    <MenuFormPage
      titles={[t("enterGame.headers.default")]}
      subHeaders={[t("enterGame.subheaders.default")]}
      submitText={t("button.join", { ns: "common" })}
      ready={true}
      submit={handleJoin}
    >
      <div className={"flex-grow flex flex-col justify-center items-center"}>
        {lastRoomCode && (
          <div className={"margin-b-4 text-lg"}>
            ({t("enterGame.body.autoPopulates")})
          </div>
        )}
        <input
          className={"text-4xl leading-snug bg-gray-drk shadow-basic"}
          type={codeHidden ? "password" : "text"}
          minLength={4}
          maxLength={4}
          size={4}
          placeholder={t("label.code", { ns: "common" })}
          defaultValue={lastRoomCode ?? undefined}
          onChange={(e) => setRoomCode(e.target.value)}
        />
        <div className="flex items-center space-x-2 mt-2">
          <Label
            htmlFor="hide-code"
            className="text-xl select-none cursor-pointer"
          >
            {t("enterGame.body.hideCode")}
          </Label>
          <Checkbox
            id="hide-code"
            checked={codeHidden}
            onCheckedChange={(checked) => handleCheck(checked as boolean)}
          />
        </div>
      </div>
    </MenuFormPage>
  );
};

export default EnterGameCode;
